import { fetchServer } from "@/features/utils/fetch-server";
import { useMutation } from "@tanstack/react-query";
import i18n from "@/features/utils/i18n";

export interface PublicWebUser {
  hash: string; // HMAC of the contact (email preferred, else phone)
  email?: string;
  phone?: string;
  given_name?: string;
  family_name?: string;
}

export interface StartPublicSessionParams {
  connector: string;
  language?: string;
  timezone?: string;
  origin?: string;
  device?: string;
  user?: PublicWebUser;
}

// Create a fresh public web-chat session for the embeddable widget and return its
// public id. Used by the `/c/new` entry point that the "Talk with Leo" iframe opens.
export function useStartPublicSession() {
  return useMutation({
    mutationFn: async ({
      connector,
      language,
      timezone,
      origin,
      device,
      user,
    }: StartPublicSessionParams): Promise<{ public_id: string }> => {
      const response = await fetchServer(`/api/sessions/v1/public/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          connector,
          language,
          timezone,
          origin,
          device,
          user,
        }),
        // `fetchServer` answers a 403 by sending the whole window to the back-office
        // login — right for the app, and here it navigated the widget's iframe away from
        // the conversation (a site missing from the connector's allowed origins). The
        // error is the chat's to show.
        ignoreError: true,
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(
          body.error || i18n.t("chat.start_failed", { ns: "public" }),
        );
      }

      return response.json();
    },
  });
}
